import { apiClient } from "./api";
import {
  ApiResponse,
  JournalEntry,
  JournalBuildResult,
  JournalPostResult,
  DimensionCodes,
  PostingSnapshot,
  JournalDraft,
  JournalTotals,
  JournalValidationResult,
} from "../types";

export const journalService = {
  /**
   * Build journal entries from opportunities
   */
  async buildJournalEntries(batchName: string): Promise<ApiResponse<JournalBuildResult>> {
    const response = await apiClient.post<ApiResponse<JournalBuildResult>>("/api/journal/build", {
      batchName,
    });
    return response.data;
  },

  /**
   * Post journal entries to Sage Intacct
   */
  async postJournalEntries(
    entries: JournalEntry[],
    journalId: string,
    description: string,
    postDate: string,
    location: string,
    batchName?: string,
    totals?: JournalTotals,
    opportunityCount?: number,
    batchAmount?: number,
  ): Promise<ApiResponse<JournalPostResult>> {
    const response = await apiClient.post<ApiResponse<JournalPostResult>>("/api/journal/post", {
      entries,
      journalId,
      description,
      postDate,
      location,
      batchName,
      totals,
      opportunityCount,
      batchAmount,
    });
    return response.data;
  },

  /**
   * Validate journal entries
   */
  async validateJournalEntries(
    entries: JournalEntry[],
  ): Promise<ApiResponse<JournalValidationResult>> {
    const response = await apiClient.post<ApiResponse<JournalValidationResult>>(
      "/api/journal/validate",
      { entries },
    );
    return response.data;
  },

  /**
   * Get dimension codes for dropdowns (locations, departments, projects, etc.)
   */
  async getDimensionCodes(): Promise<ApiResponse<DimensionCodes>> {
    const response = await apiClient.get<ApiResponse<DimensionCodes>>("/api/sage/dimension-codes");
    return response.data;
  },

  /**
   * Get the posting snapshot for a batch (recorded at time of posting)
   */
  async getPostingSnapshot(batchName: string): Promise<ApiResponse<PostingSnapshot>> {
    const response = await apiClient.get<ApiResponse<PostingSnapshot>>(
      `/api/journal/snapshot/${encodeURIComponent(batchName)}`,
    );
    return response.data;
  },

  /**
   * Save (upsert) an in-progress journal draft for a batch, so it can be
   * resumed later if the user leaves the posting screen.
   */
  async saveDraft(
    batchName: string,
    description: string,
    postDate: string,
    location: string,
    entries: JournalEntry[],
  ): Promise<ApiResponse<JournalDraft>> {
    const response = await apiClient.post<ApiResponse<JournalDraft>>("/api/journal/draft", {
      batchName,
      description,
      postDate,
      location,
      entries,
    });
    return response.data;
  },

  /**
   * Get the saved journal draft for a batch, if one exists
   */
  async getDraft(batchName: string): Promise<ApiResponse<JournalDraft>> {
    const response = await apiClient.get<ApiResponse<JournalDraft>>(
      `/api/journal/draft/${encodeURIComponent(batchName)}`,
    );
    return response.data;
  },

  /**
   * Delete the saved journal draft for a batch (used by "Start Again")
   */
  async deleteDraft(batchName: string): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/journal/draft/${encodeURIComponent(batchName)}`,
    );
    return response.data;
  },
};
